import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { BASE_URL } from "../config/config";
import ProductDetailsModal from "../components/ProductDetails/ProductDetailsModal";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Cart = () => {
  const { cartItems, increaseQuantity, decreaseQuantity, deleteCartItem } =
    useCart();
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const handleQuickView = (e, product) => {
    e.preventDefault();
    setSelectedProduct(product);
    setShowModal(true);
  };
  
  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    if (isLoggedIn) {
      navigate("/checkout");
    } else {
      navigate("/login", { state: { from: "/checkout" } });
    }
  };

  const handleProceed = () => {
    const totalAmount = cartItems.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0
    );

    if (totalAmount > 50000) {
      alert("The transaction amount exceeds your assigned slab limit. Please contact support to increase your limit.");
      return;
    }

    handleCheckout();
  };

  const goToShop = () => {
    navigate("/shop");
  };

  return (
    <>
      <section className="cart-section py-5 bg-light">
        <div className="container">
          {/* Breadcrumb */}
          <div className="breadcrumb-container p-3 bg-white mb-4 rounded shadow-sm">
            <p className="mb-0" style={{ fontSize: "14px", color: "#666" }}>
              <span style={{ color: "#1d3349ff", cursor: "pointer" }}>
                <Link to="/">Home</Link>
              </span>{" "}
              / <span className="text-secondary fw-semibold">Cart</span>
            </p>
          </div>

          {cartItems.length === 0 ? (
            // Empty Cart Design
            <div className="text-center py-5 bg-white rounded shadow-sm">
              <img
                src="https://cdn-icons-png.flaticon.com/512/2038/2038854.png"
                alt="Empty Cart"
                style={{ width: "150px", marginBottom: "20px" }}
              />
              <h3 className="mb-3">Your cart is empty!</h3>
              <p className="text-muted mb-4">
                Looks like you haven't added anything yet.
              </p>
              <button
                className="btn btn-dark px-4 py-2 rounded-pill"
                onClick={goToShop}
              >
                🛍️ Start Shopping
              </button>
            </div>
          ) : (
            // Cart Details
            <div className="row">
              {/* Cart Items */}
              <div className="col-lg-8">

                <div className="bg-white rounded-4 shadow-sm p-4">

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="fw-bold mb-0">
                      Shopping Cart
                    </h4>

                    <span className="badge bg-dark rounded-pill px-3 py-2">
                      {cartItems.length} Item{cartItems.length > 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="table-responsive">

                    <table className="table align-middle">

                      <thead>
                        <tr>
                          <th width="140">Product</th>
                          <th>Course</th>
                          <th width="170" className="text-center">Quantity</th>
                          <th width="130" className="text-end">Price</th>
                          <th width="140" className="text-end">Total</th>
                          <th width="70"></th>
                        </tr>
                      </thead>

                      <tbody>

                        {cartItems.map((product) => (

                          <tr key={`${product.product_id}-${product.size}`}>

                            <td width="130">

                              <img
                                src={`${BASE_URL}/uploads/${product.image}`}
                                alt={product.product_name}
                                className="img-fluid rounded-3 border"
                                style={{
                                  width: "110px",
                                  height: "110px",
                                  objectFit: "cover",
                                  padding: "5px"
                                }}
                              />

                            </td>

                            <td>

                              <Link
                                onClick={(e) => handleQuickView(e, product)}
                                style={{
                                  textDecoration: "none",
                                  color: "#111",
                                  fontWeight: 600,
                                  fontSize: "17px"
                                }}
                              >
                                {product.product_name}
                              </Link>

                              <div className="mt-2">
                                <span className="badge bg-light text-dark border">
                                  Size : {product.size}
                                </span>
                              </div>

                            </td>

                            <td className="text-center">

                              <div
                                className="d-inline-flex align-items-center border rounded-pill overflow-hidden"
                              >

                                <button
                                  className="btn btn-light border-0 px-3"
                                  onClick={() => decreaseQuantity(product)}
                                >
                                  −
                                </button>

                                <span
                                  className="px-3 fw-bold"
                                  style={{ minWidth: 35 }}
                                >
                                  {product.quantity}
                                </span>

                                <button
                                  className="btn btn-light border-0 px-3"
                                  onClick={() => increaseQuantity(product)}
                                >
                                  +
                                </button>

                              </div>

                            </td>

                            <td className="text-end fw-semibold text-success">
                              ₹{Number(product.price).toLocaleString("en-IN")}
                            </td>

                            <td className="text-end fw-bold">
                              ₹{(product.price * product.quantity).toLocaleString("en-IN")}
                            </td>

                            <td className="text-center">

                              <button
                                className="btn btn-outline-danger btn-sm rounded-circle"
                                onClick={() =>
                                  deleteCartItem(
                                    product.product_id,
                                    product.size,
                                    product.cart_id
                                  )
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>

                            </td>

                          </tr>

                        ))}

                      </tbody>

                    </table>

                  </div>

                </div>

              </div>

              {/* Cart Summary */}

              <div className="col-lg-4 mt-4 mt-lg-0">

                <div
                  className="bg-white rounded-4 shadow-sm p-4"
                  style={{ position: "sticky", top: 20 }}
                >

                  <h4 className="fw-bold mb-4">
                    Cart Summary
                  </h4>

                  <div className="d-flex justify-content-between mb-3">
                    <span>Subtotal</span>

                    <strong>
                      ₹
                      {cartItems
                        .reduce(
                          (acc, item) => acc + item.price * item.quantity,
                          0
                        )
                        .toLocaleString("en-IN")}
                    </strong>
                  </div>

                  {/* <div className="d-flex justify-content-between mb-3">
                    <span>Shipping</span>
                    <span className="text-success fw-semibold">
                      ₹50
                    </span>
                  </div> */}

                  <hr />

                  <div className="d-flex justify-content-between fs-5 fw-bold mb-4">
                    <span>Total</span>

                    <span className="text-success">
                      ₹
                      {(
                        cartItems.reduce(
                          (acc, item) => acc + item.price * item.quantity,
                          0
                        )
                      ).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <button
                    className="btn btn-dark rounded-pill w-100 py-3 fw-semibold"
                    onClick={handleProceed}
                  >
                    Proceed to Checkout
                  </button>

                  <div className="mt-4 small text-muted">

                    <div className="mb-2">
                      <i className="fa fa-check-circle text-success me-2"></i>
                      Secure Checkout
                    </div>

                    <div className="mb-2">
                      <i className="fa fa-check-circle text-success me-2"></i>
                      Fast Delivery
                    </div>

                    <div>
                      <i className="fa fa-check-circle text-success me-2"></i>
                      100% Genuine Products
                    </div>

                  </div>

                </div>

              </div>

            </div>
          )}
        </div>
      </section>
      <ProductDetailsModal
        show={showModal}
        onClose={() => setShowModal(false)}
        product={selectedProduct}
      />
    </>
  );
};

export default Cart;
